import { useState, useEffect } from "react";
import axios from "axios";
import { FileSpreadsheet, Download, Clock, Users, Loader2, FolderOpen } from "lucide-react";
import { Button } from "./components/ui/button";
import { toast } from "sonner";

import { API } from "./apiConfig";

export function PastReports({ isOpen, onClose, timezone = "America/Los_Angeles" }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    axios
      .get(`${API}/reports`, { withCredentials: true })
      .then((r) => setReports(r.data.reports || []))
      .catch(() => setReports([]))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const handleDownload = (fileId, fileName) => {
    const link = document.createElement("a");
    link.href = `${API}/reports/${fileId}/download`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Download started", { description: fileName });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="past-reports-modal">
      <div className="absolute inset-0 bg-slate-50/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-slate-50 border border-slate-200 rounded-2xl shadow-[0_0_40px_-12px_rgba(0,0,0,0.5)] max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2 text-[#0A1F3B]">
            <FolderOpen size={18} />
            <h2 className="font-heading text-lg font-bold">Past Reports</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-[#0A1F3B] transition-colors text-sm">
            Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="text-[#0A1F3B] animate-spin" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12" data-testid="no-reports">
              <FileSpreadsheet size={32} className="text-slate-500 mx-auto mb-3" />
              <p className="text-sm text-slate-600 mb-1">No reports yet</p>
              <p className="text-xs text-slate-500">
                Reports are saved when you generate an Excel export.
              </p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="reports-list">
              {reports.map((r) => (
                <div
                  key={r.file_id}
                  className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg hover:border-slate-200/80 transition-colors"
                  data-testid={`report-${r.file_id}`}
                >
                  <div className="w-9 h-9 bg-[#0A1F3B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileSpreadsheet size={16} className="text-[#0A1F3B]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#0A1F3B] truncate">{r.file_name}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      {r.date_range && (
                        <span className="text-[10px] text-slate-600 flex items-center gap-1">
                          <Clock size={10} /> {r.date_range}
                        </span>
                      )}
                      <span className="text-[10px] text-slate-600 flex items-center gap-1">
                        <Users size={10} /> {r.employee_count} emp
                      </span>
                      <span className="text-[10px] text-slate-500">
                        {new Date(r.created_at).toLocaleString("en-US", { timeZone: timezone, month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleDownload(r.file_id, r.file_name)}
                    className="bg-slate-200 text-[#0A1F3B] hover:bg-[#2A5080] h-7 text-xs flex-shrink-0"
                    data-testid={`download-report-${r.file_id}`}
                  >
                    <Download size={12} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
